import { useState, useEffect } from "react";

const LOCATIONS = [
  { name: "Orebić", lat: 42.9794, lon: 17.1781 },
  { name: "Korčula", lat: 42.9597, lon: 17.1358 },
];

const API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;

// Weather icon mapping from OpenWeatherMap codes
const getWeatherIcon = (iconCode) => {
  const iconMap = {
    "01d": "☀️", // clear sky day
    "01n": "🌙", // clear sky night
    "02d": "⛅", // few clouds day
    "02n": "☁️", // few clouds night
    "03d": "☁️", // scattered clouds
    "03n": "☁️",
    "04d": "☁️", // broken clouds
    "04n": "☁️",
    "09d": "🌧️", // shower rain
    "09n": "🌧️",
    "10d": "🌦️", // rain day
    "10n": "🌧️", // rain night
    "11d": "⛈️", // thunderstorm
    "11n": "⛈️",
    "13d": "❄️", // snow
    "13n": "❄️",
    "50d": "🌫️", // mist
    "50n": "🌫️",
  };
  return iconMap[iconCode] || "☀️";
};

export const WeatherWidget = ({ isScrolled = false }) => {
  const [weatherData, setWeatherData] = useState({});
  const [currentLocationIndex, setCurrentLocationIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [apiAvailable, setApiAvailable] = useState(true);

  // Fetch weather data for all locations
  useEffect(() => {
    const fetchWeather = async () => {
      if (!API_KEY) {
        setApiAvailable(false);
        setLoading(false);
        return;
      }

      try {
        const promises = LOCATIONS.map(async (location) => {
          const response = await fetch(
            `https://api.openweathermap.org/data/2.5/weather?lat=${location.lat}&lon=${location.lon}&appid=${API_KEY}&units=metric&lang=hr`
          );
          if (!response.ok) {
            throw new Error(`Weather fetch failed: ${response.status}`);
          }
          const data = await response.json();
          return { name: location.name, data };
        });

        const results = await Promise.all(promises);
        const weatherObj = {};
        results.forEach((result) => {
          weatherObj[result.name] = result.data;
        });
        setWeatherData(weatherObj);
        setApiAvailable(true);
        setLoading(false);
      } catch (err) {
        console.error("Weather fetch error:", err);
        setApiAvailable(false);
        setLoading(false);
      }
    };

    fetchWeather();

    // Refresh weather every 10 minutes
    const refreshInterval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, []);

  // Rotate between locations every 2 seconds
  useEffect(() => {
    const rotateInterval = setInterval(() => {
      setCurrentLocationIndex((prev) => (prev + 1) % LOCATIONS.length);
    }, 2000);
    return () => clearInterval(rotateInterval);
  }, []);

  const currentLocation = LOCATIONS[currentLocationIndex];
  
  // If API is not available, show placeholder with location name
  if (!apiAvailable || loading) {
    // Still show widget but with placeholder
    return (
      <div
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-300 ${
          isScrolled
            ? "bg-[#f1f5f9] text-[#0F172A]"
            : "bg-white/20 text-white backdrop-blur-sm"
        }`}
        data-testid="weather-widget"
      >
        <span className="text-lg leading-none">☀️</span>
        <div className="flex flex-col items-start">
          <span className="text-xs font-medium leading-tight opacity-80">
            {currentLocation.name}
          </span>
          <span className="text-sm font-semibold leading-tight">
            {loading ? "..." : "—"}
          </span>
        </div>
      </div>
    );
  }

  const weather = weatherData[currentLocation.name];

  if (!weather) {
    return null;
  }

  const temp = Math.round(weather.main?.temp);
  const icon = getWeatherIcon(weather.weather?.[0]?.icon);

  return (
    <div
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-300 ${
        isScrolled
          ? "bg-[#f1f5f9] text-[#0F172A]"
          : "bg-white/20 text-white backdrop-blur-sm"
      }`}
      data-testid="weather-widget"
    >
      <span className="text-lg leading-none">{icon}</span>
      <div className="flex flex-col items-start">
        <span className="text-xs font-medium leading-tight opacity-80">
          {currentLocation.name}
        </span>
        <span className="text-sm font-semibold leading-tight">{temp}°C</span>
      </div>
    </div>
  );
};
